import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link as MuiLink,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { llmModels } from './modelData';

// Helper function to generate benchmark categories from llmModels
const generateBenchmarkCategoriesFromModelData = (models) => {
  const benchmarks = {}; // Key: metric.name, Value: { id, name, description, models }

  // Optional: provide descriptions for known metrics to make the UI more informative
  const metricDescriptions = {
    "GPQA Diamond": "Complex benchmark evaluating quality and reliability across biology, physics, and chemistry (e.g., GPQA). Higher is better.",
    "AIME 2024": "American Invitational Mathematics Examination, tests advanced mathematical problem-solving. Higher is better.",
    "Code SWE": "SWE-bench evaluates systems on their ability to solve real-world GitHub issues. Higher is better.",
    "HumanEval": "Evaluates Python code generation capabilities. Higher is better.",
    "MMLU": "Tests knowledge across 57 subjects including science, humanities, math, and more. Higher is better.",
    "Speed t/s": "Processing speed in tokens per second. Higher is better.",
    "Tool Use": "Evaluates the model's capability to use external tools or APIs. Higher is better.",
    "SQL Gen": "Evaluates the model's ability to generate SQL queries. Higher is better.",
    "Data Analysis": "Evaluates performance on data analysis tasks. Higher is better.",
    "RAG Score": "Evaluates performance in Retrieval Augmented Generation tasks. Higher is better."
    // Add more descriptions as needed
  };

  models.forEach(model => {
    model.keyMetrics?.forEach(metric => {
      const metricName = metric.name;
      if (!benchmarks[metricName]) {
        benchmarks[metricName] = {
          id: metricName.toLowerCase().replace(/[^a-z0-9]/g, '_'), // Sanitize for ID
          name: metricName,
          description: metricDescriptions[metricName] || `Benchmark scores for ${metricName}. Higher is generally better.`,
          models: []
        };
      }
      benchmarks[metricName].models.push({
        name: model.name,
        score: metric.score // Scores are kept as strings
      });
    });
  });

  // Sort models within each category by score (descending, non-numeric last), then by name
  return Object.values(benchmarks).map(category => {
    category.models.sort((a, b) => {
      const scoreA = parseFloat(String(a.score).replace(/[>%~]/g, ''));
      const scoreB = parseFloat(String(b.score).replace(/[>%~]/g, ''));

      if (isNaN(scoreA) && isNaN(scoreB)) return a.name.localeCompare(b.name);
      if (isNaN(scoreA)) return 1; // Non-numeric scores (like 'High', 'N/A') go last
      if (isNaN(scoreB)) return -1;
      if (scoreB !== scoreA) return scoreB - scoreA;
      return a.name.localeCompare(b.name);
    });
    return category;
  }).sort((a,b) => a.name.localeCompare(b.name)); // Sort categories alphabetically
};

const Benchmarks = () => {
  const [viewMode, setViewMode] = useState('comparison');

  const benchmarkCategories = useMemo(() => generateBenchmarkCategoriesFromModelData(llmModels), []);

  // Get unique model names from llmModels for selection
  const allModelsFromData = useMemo(() => 
    [...new Set(llmModels.map(model => model.name))].sort(), 
    [] // llmModels is static, so this runs once
  );

  // Initialize selectedModels with a few from allModelsFromData or keep it empty
   const [selectedModels, setSelectedModels] = useState(() => {
     const defaultSelection = [
      'Claude 3.7',
      'GPT-4.5', // Assuming GPT-4.1 maps to GPT-4.5 or similar in modelData
      'Gemini 1.5 Pro',
      'Llama 3 70B',
      'WizardLM 28x22B', // Check if this exact name exists in modelData
      'Grok 3 [Beta]',
      'Phi-3 Medium 128K'
    ];
    // Filter to ensure only existing models are pre-selected
    return defaultSelection.filter(name => allModelsFromData.includes(name));
  });

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleModelToggle = (modelName) => {
    setSelectedModels(prev => {
      if (prev.includes(modelName)) {
        return prev.filter(m => m !== modelName);
      } else {
        return [...prev, modelName];
      }
    });
  };

  // Get unique model names across all benchmarks
  const allBenchmarkModelNames = useMemo(() => 
    [...new Set(benchmarkCategories.flatMap(category => category.models.map(model => model.name)))].sort()
  , [benchmarkCategories]);

  const ComparisonTable = () => {
    // Filter categories to only show ones that have data for selected models
    const relevantCategories = benchmarkCategories.filter(category => 
      selectedModels.some(modelName => 
        category.models.some(m => m.name === modelName)
      )
    ).sort((a,b) => a.name.localeCompare(b.name)); // Sort columns alphabetically

    return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', minWidth: '200px' }}>Model</TableCell>
              {relevantCategories.map(category => (
              <TableCell key={category.id} align="right" sx={{ fontWeight: 'bold' }}>
                {category.name}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ minWidth: '150px', fontWeight: 'bold' }}>Input Price</TableCell>
            <TableCell align="right" sx={{ minWidth: '150px', fontWeight: 'bold' }}>Output Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedModels.map(modelName => {
            const modelInfo = llmModels.find(m => m.name === modelName);
            
            let inputPriceDisplay = 'N/A';
            let outputPriceDisplay = 'N/A';

            if (modelInfo) {
              if (modelInfo.isOpenSource) {
                inputPriceDisplay = 'N/A (Open Source)';
                outputPriceDisplay = 'N/A (Open Source)';
              } else {
                if (modelInfo.inputPrice && modelInfo.inputPrice !== "0.00") {
                  inputPriceDisplay = `$${parseFloat(modelInfo.inputPrice).toFixed(2)} / M tokens`;
                } else if (modelInfo.inputPrice === "0.00") {
                  inputPriceDisplay = 'Free';
                }
                
                if (modelInfo.outputPrice && modelInfo.outputPrice !== "0.00") {
                  outputPriceDisplay = `$${parseFloat(modelInfo.outputPrice).toFixed(2)} / M tokens`;
                } else if (modelInfo.outputPrice === "0.00") {
                  outputPriceDisplay = 'Free';
                }
              }
            }
            
            return (
              <TableRow key={modelName} hover>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                  <RouterLink 
                    to={`/models/${modelInfo?.id}`}
                    style={{ textDecoration: 'none', color: '#1976d2' }}
                  >
                    {modelName}
                  </RouterLink>
                </TableCell>
                {relevantCategories.map(category => {
                  const modelData = category.models.find(m => m.name === modelName);
                  // Check if this model has the top score in this category
                  const isTopScore = modelData && category.models.length > 0 &&
                                   parseFloat(String(modelData.score).replace(/[>%~]/g, '')) === parseFloat(String(category.models[0].score).replace(/[>%~]/g, '')) &&
                                   !isNaN(parseFloat(String(modelData.score).replace(/[>%~]/g, '')));

                  return (
                    <TableCell key={category.id} align="right">
                      {modelData ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {modelData.score}
                          {isTopScore && (
                            <Chip 
                              size="small" 
                              label="Best" 
                              color="primary"
                              sx={{ height: '20px', fontSize: '0.7rem', padding: '0 4px' }} 
                            />
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {inputPriceDisplay}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {outputPriceDisplay}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    );
  };

  const metaDescription = "Comprehensive benchmark results and comparisons for leading AI language models. View performance metrics across multiple categories and compare model capabilities.";

  // Create JSON-LD schema data for benchmarks
  const jsonLdData = {
    "@context": "https://schema.org/",
    "@type": "Dataset",
    "name": "AI Language Model Benchmarks",
    "description": metaDescription,
    "url": window.location.href,
    "keywords": ["AI", "language models", "benchmarks", "GPQA", "AIME", "code generation", "performance metrics"],
    "creator": {
      "@type": "Organization",
      "name": "AI Models Directory"
    },
    "variableMeasured": benchmarkCategories.map(category => ({
      "@type": "PropertyValue",
      "name": category.name,
      "description": category.description
    }))
  };

  return (
    <>
      <Helmet>
        <title>AI Model Benchmarks - Performance Metrics and Comparisons</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content="AI Model Benchmarks - Performance Metrics" />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content="AI Model Benchmarks - Performance Metrics" />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="/benchmarks" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      </Helmet>

      <Container maxWidth="lg" component="main">
        <Box sx={{ my: 4 }} component="article">
          <header>
            <Typography variant="h1" sx={{ fontSize: 'h3.fontSize' }} gutterBottom>
              AI Model Benchmarks
            </Typography>
            <Typography variant="h2" sx={{ fontSize: 'h6.fontSize' }} color="text.secondary" gutterBottom>
              Compare performance metrics across leading language models
            </Typography>
          </header>

          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
            >
              <ToggleButton value="individual" aria-label="individual view">
                Individual Benchmarks
              </ToggleButton>
              <ToggleButton value="comparison" aria-label="comparison view">
                Compare Models
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === 'comparison' ? (
            <section aria-labelledby="model-comparison-title">
              <Typography variant="h2" sx={{ fontSize: 'h6.fontSize' }} gutterBottom id="model-comparison-title">
                Select Models to Compare
              </Typography>
              <Paper component="div" sx={{ p: 2, mb: 3, overflowX: 'auto' }}>
              <FormGroup row sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {allModelsFromData.map(modelName => (
                  <FormControlLabel
                    key={modelName}
                    control={
                      <Checkbox
                        checked={selectedModels.includes(modelName)}
                        onChange={() => handleModelToggle(modelName)}
                        inputProps={{ 'aria-label': `Select ${modelName} for comparison` }}
                      />
                    }
                    label={modelName}
                    sx={{ minWidth: '250px' }}
                  />
                ))}
              </FormGroup>
              </Paper>
              <Paper component="div" sx={{ mt: 3, overflowX: 'auto' }}>
                <ComparisonTable />
              </Paper>
            </section>
          ) : (
            <section aria-label="Individual benchmarks">
              {benchmarkCategories.map((category, index) => (
                <Box key={category.name} sx={{ mb: 6 }} component="section" aria-labelledby={`category-${index}-title`}>
                  <Typography variant="h2" sx={{ fontSize: 'h5.fontSize' }} gutterBottom id={`category-${index}-title`}>
                    {category.name}
                  </Typography>
                  <Typography variant="body1" paragraph component="p">
                    {category.description}
                  </Typography>
                  
                  <TableContainer component={Paper}>
                    <Table aria-label={`${category.name} benchmark scores`}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Model</TableCell>
                          <TableCell align="right">Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {category.models.map((model) => (
                          <TableRow key={model.name}>
                            <TableCell component="th" scope="row">
                              <RouterLink 
                                to={`/models/${llmModels.find(m => m.name === model.name)?.id}`}
                                style={{ textDecoration: 'none', color: '#1976d2' }}
                              >
                                {model.name}
                              </RouterLink>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={model.score}
                                color={category.models.length > 0 && model.name === category.models[0].name && !isNaN(parseFloat(String(model.score).replace(/[>%~]/g, ''))) ? "primary" : "default"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {category.references && (
                    <Box sx={{ mt: 2 }} component="div">
                      <Typography variant="h3" sx={{ fontSize: 'subtitle1.fontSize' }} gutterBottom>
                        References
                      </Typography>
                      <List aria-label={`${category.name} benchmark references`}>
                        {category.references.map((ref, idx) => (
                          <React.Fragment key={idx}>
                            <ListItem>
                              <ListItemText
                                primary={ref.title}
                                secondary={
                                  <>
                                    <Typography variant="body2" component="p">
                                      {ref.description}
                                    </Typography>
                                    <Link
                                      href={ref.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Read more about ${ref.title}`}
                                    >
                                      Read Paper
                                    </Link>
                                  </>
                                }
                              />
                            </ListItem>
                            {idx < category.references.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              ))}
            </section>
          )}
        </Box>
      </Container>
    </>
  );
};

export default Benchmarks; 